import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { AnchorButton } from "@blueprintjs/core";

import { Heading } from "@components/Headings";
import { useBasePath, useOrg, useRepos } from "@hooks";
import { Feature } from "@utils";

import { AddProjectsButton } from "@pages/overview/components/AddProjectsButton";
import { AddProjectsDialog } from "@pages/overview/components/AddProjectsDialog";
import { Repo } from "@pages/projects/types";
import { partitionSetupRepos } from "@pages/projects/utils";

import { fetchDemoRepo } from "../../api";
import { DemoProjectTable } from "../DemoProjectTable";
import { NoProjectZeroState } from "../NoProjectZeroState";
import { ProjectListPanel } from "../ProjectList";

const ProjectListHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ReposPanel: React.FC = () => {
  const { data: repos } = useRepos();
  const [demoRepo, setDemoRepo] = useState<Repo>();
  const [editRepos, setEditRepos] = useState<boolean>(false);

  const org = useOrg();
  const history = useHistory();
  const basePath = useBasePath();

  const { setupRepos } = useMemo(
    () => partitionSetupRepos(repos ?? []),
    [repos]
  );

  useEffect(() => {
    // if there are no projects, load the stats for the demo project
    if (!org || setupRepos.length > 0) return;
    fetchDemoRepo(org.id).then((resp) => {
      setDemoRepo(resp.repo);
    });
  }, [org, setupRepos]);

  return (
    <>
      <Helmet>
        <title>Projects | Semgrep</title>
      </Helmet>

      <AddProjectsDialog />
      {setupRepos && setupRepos.length > 0 ? (
        <>
          <ProjectListHeader>
            <Heading>Projects</Heading>
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <AnchorButton
                onClick={() => setEditRepos(!editRepos)}
                style={{ height: "2em" }}
              >
                {editRepos ? "Cancel" : "Edit projects"}
              </AnchorButton>
              {org && Feature.hasInvites({ org }) && (
                <AnchorButton
                  onClick={() => history.push(`${basePath}/settings/members`)}
                  style={{ height: "2em" }}
                >
                  Add team members
                </AnchorButton>
              )}
              <div>
                <AddProjectsButton title="Scan new project" />
              </div>
            </div>
          </ProjectListHeader>
          <ProjectListPanel repos={setupRepos} rowSelect={editRepos} />
        </>
      ) : (
        <>
          <Heading>Projects</Heading>
          {demoRepo && (
            <div style={{ paddingBottom: "40px" }}>
              <DemoProjectTable demoRepo={demoRepo} />
            </div>
          )}
          <NoProjectZeroState />
        </>
      )}
    </>
  );
};
